'use client'

import { useState } from 'react'
import Image from 'next/image'
import { Layout } from 'lucide-react'
import { cn, generateCdnImageUrl } from '@/lib/utils'

interface OptimizedTemplateImageProps {
  src?: string
  alt: string
  className?: string
  priority?: boolean
}

export const OptimizedTemplateImage = ({
  src,
  alt,
  className,
  priority = false,
}: OptimizedTemplateImageProps) => {
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(true)

  // 如果没有图片 URL 或图片加载失败，显示占位符
  if (!src || imageError) {
    return (
      <div
        className={cn(
          'w-full h-full flex items-center justify-center',
          'bg-gradient-to-br from-purple-500/10 to-cyan-400/10',
          className,
        )}
      >
        <Layout
          className="w-8 h-8 text-cyan-400/40"
          data-testid="layout-icon"
        />
      </div>
    )
  }

  // 使用 CDN 优化图片
  const optimizedSrc = generateCdnImageUrl(src, {
    width: 430,
    height: 240, // 16:9 aspect ratio
    quality: 100,
  })

  return (
    <div className={cn('relative w-full h-full overflow-hidden', className)}>
      {/* Loading shimmer effect - shows while image loads */}
      {imageLoading && (
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-white/5 to-white/[0.02] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_2s_infinite]" />
        </div>
      )}

      {/* 优化的图片 */}
      <Image
        src={optimizedSrc}
        alt={alt}
        fill
        className={cn(
          'object-cover transition-all duration-300',
          imageLoading ? 'opacity-0' : 'opacity-100',
        )}
        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
        priority={priority}
        loading={priority ? 'eager' : 'lazy'}
        onLoad={() => setImageLoading(false)}
        onError={() => {
          setImageError(true)
          setImageLoading(false)
        }}
        unoptimized={true}
        // 添加 blur placeholder
        placeholder="blur"
        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
      />
    </div>
  )
}
